import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { format, isValid } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { Bed, Clock, Scissors } from 'lucide-react'

import { PetFormData } from '@/features/booking/types/booking.types'
import { usePeriodsForReservation } from '@/shared/hooks/use-periods-for-reservation'
import { isHairdressingService } from '@/shared/types/service-checkers'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { ServiceItem } from '@/shared/ui/service-item'
import { formatCurrency } from '@/shared/utils/format'
import { calculateNights, calculateTotalReservationPrice, hasHighSeasonDays, type PetPriceBreakdown } from '@/shared/utils/pricing'
import { ClientClassification } from '@monorepo/functions/src/types/client'
import { AdditionalService } from '@monorepo/functions/src/types/services'

import { useHotelAvailability } from './HotelAvailabilityContext'

interface BookingSummaryProps {
    dates: {
        from: Date
        to: Date
    } | null
    pets: PetFormData[]
    services: AdditionalService[]
    clientClassification?: ClientClassification
}

export function BookingSummary({ dates, pets, services, clientClassification }: BookingSummaryProps) {
    const { t, i18n } = useTranslation()
    const checkIn = dates && isValid(dates.from) ? format(dates.from, 'yyyy-MM-dd') : undefined
    const checkOut = dates && isValid(dates.to) ? format(dates.to, 'yyyy-MM-dd') : undefined
    const { periods, loading: periodsLoading } = usePeriodsForReservation(checkIn, checkOut)
    const { highSeasonPeriods } = useHotelAvailability()
    const dateLocale = useMemo(() => (i18n.language === 'en' ? enUS : es), [i18n.language])
    const nights = calculateNights(dates)
    const priceBreakdown = useMemo(() => {
        if (!dates || periodsLoading || !periods.length) {
            return { total: 0, petsBreakdown: {} as Record<number, PetPriceBreakdown> }
        }
        return calculateTotalReservationPrice(
            dates,
            pets,
            services,
            periods,
            clientClassification,
            highSeasonPeriods,
        )
    }, [dates, periods, periodsLoading, pets, services, clientClassification, highSeasonPeriods])
    const hasHighSeason = dates ? hasHighSeasonDays(dates, highSeasonPeriods) : false

    // Calcular precio por día (total dividido por número de días o 1 si es guardería)
    const daysCount = nights === 0 ? 1 : nights
    const pricePerDay = priceBreakdown.total / daysCount

    // Group services by pet
    const driverService = services.find(s => s.type === 'driver')
    const hasHairdressingService = services.some(isHairdressingService)
    const servicesByPet = pets.map((pet, index) => ({
        pet,
        services: services.filter(s => s.type !== 'driver' && 'petIndex' in s && s.petIndex === index),
        breakdown: priceBreakdown.petsBreakdown[index] ?? {
            basePrice: 0,
            servicesPrice: 0,
            subtotal: 0,
            total: 0,
            services: [],
        },
    }))

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('booking.summary.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
                {/* Dates */}
                {dates && isValid(dates.from) && isValid(dates.to) && (
                    <div>
                        <div className='space-y-2 text-muted-foreground'>
                            <div className='flex items-center gap-2'>
                                <Clock className='h-4 w-4' />
                                <p>
                                    {t('booking.summary.dates.checkIn', {
                                        date: format(dates.from, 'PPP', { locale: dateLocale }),
                                    })}
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Clock className='h-4 w-4' />
                                <p>
                                    {t('booking.summary.dates.checkOut', {
                                        date: format(dates.to, 'PPP', { locale: dateLocale }),
                                    })}
                                </p>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Bed className='h-4 w-4' />
                                <p className='text-sm'>
                                    {nights === 0
                                        ? t('booking.summary.dates.daycare')
                                        : t('booking.summary.dates.nights', { nights })}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Driver Service */}
                {driverService && (
                    <div>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center justify-between'>
                                <ServiceItem service={driverService} />
                                <span className='text-xs text-yellow-600'>
                                    {t('booking.summary.transport.fromPrice', 'Desde {{price}}€', {
                                        price: 20,
                                    })}
                                </span>
                            </div>
                            <p className='text-xs italic text-muted-foreground'>
                                {t('booking.summary.transport.priceNote')}
                            </p>
                        </div>
                    </div>
                )}

                {/* Hairdressing Service */}
                {hasHairdressingService && (
                    <div>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-2'>
                                    <Scissors className='h-4 w-4 text-purple-500' />
                                    <span className='text-sm text-muted-foreground'>
                                        {t('booking.step3.petServices.hairdressing.title', 'Peluquería')}
                                    </span>
                                </div>
                                <span className='text-xs text-yellow-600'>
                                    {t('booking.summary.hairdressing.priceNote', 'Según servicio', {})}
                                </span>
                            </div>
                            <p className='text-xs italic text-muted-foreground'>
                                {t(
                                    'booking.summary.hairdressing.explanation',
                                    'El precio final dependerá del tipo de servicio y el tamaño de la mascota',
                                )}
                            </p>
                        </div>
                    </div>
                )}

                {/* Pets and their services */}
                {servicesByPet.map(({ pet, services, breakdown }, index) => (
                    <div key={pet.id || `pet-${index}`}>
                        <h3 className='mb-2 font-medium'>
                            {pet.name && pet.breed ? `${pet.name} (${pet.breed})` : ''}
                        </h3>
                        <div className='space-y-2 pl-4'>
                            <div className='flex items-center justify-between text-sm text-muted-foreground'>
                                <div className='flex items-center gap-2'>
                                    <Bed className='h-4 w-4' />
                                    <span>
                                        {nights === 0
                                            ? t('booking.summary.pet.daycare')
                                            : t('booking.summary.pet.stay', {
                                                  size: t(`booking.step1.petFields.sizes.${pet.size}`, pet.size),
                                              })}
                                    </span>
                                </div>
                                <span>{formatCurrency(breakdown.basePrice)}</span>
                            </div>
                            {services.map((service, serviceIndex) => (
                                <div
                                    key={`${pet.id || index}-service-${serviceIndex}-${service.type}`}
                                    className='flex items-center justify-between gap-2'
                                >
                                    <ServiceItem service={service} />
                                    <span className='text-sm text-muted-foreground'>
                                        {service.type === 'hairdressing'
                                            ? ''
                                            : formatCurrency(
                                                  breakdown.services.find(s => s.name === service.type)?.price ?? 0,
                                              )}
                                    </span>
                                </div>
                            ))}
                            <div className='flex items-center justify-between pt-1 font-medium'>
                                <span>{t('booking.summary.subtotal')}</span>
                                <span>{formatCurrency(breakdown.subtotal)}</span>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Total */}
                <div className='space-y-2 border-t pt-4'>
                    <div className='flex flex-col gap-1'>
                        {/* Advance Payment (50%) - Only shown when there are high season days */}
                        {hasHighSeason && (
                            <div className='mb-3 rounded-lg border-2 border-yellow-500 bg-yellow-50 p-3 dark:bg-yellow-950/20'>
                                <div className='flex justify-between'>
                                    <span className='text-base font-bold text-yellow-900 dark:text-yellow-200'>
                                        {t('booking.summary.advancePayment', '50% por adelantado en temporada alta')}
                                    </span>
                                    <span className='text-lg font-bold text-yellow-900 dark:text-yellow-200'>
                                        {formatCurrency(priceBreakdown.total * 0.5)}
                                    </span>
                                </div>
                                <p className='mt-1 text-xs text-yellow-800 dark:text-yellow-300'>
                                    {t(
                                        'booking.summary.advancePaymentNote',
                                        'Una vez efectuado el pago, no se admiten cancelaciones ni reembolsos, sin excepción, independientemente del motivo.',
                                    )}
                                </p>
                            </div>
                        )}
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('booking.summary.total')}</span>
                            <span className='font-semibold'>{formatCurrency(priceBreakdown.total)}</span>
                        </div>
                        <div className='flex justify-between'>
                            <span className='text-sm text-muted-foreground'>
                                {t('booking.summary.dates.pricePerDay', 'Precio por día')}
                            </span>
                            <span className='text-sm text-muted-foreground'>{formatCurrency(pricePerDay)}</span>
                        </div>
                        <div className='flex justify-end'>
                            <span className='text-xs text-muted-foreground'>
                                {t('booking.summary.vatIncluded', 'IVA incluido')}
                            </span>
                        </div>
                        {driverService && (
                            <span className='text-right text-sm text-yellow-600'>
                                {t('booking.summary.driverServiceNote')}
                            </span>
                        )}
                        {hasHairdressingService && (
                            <span className='text-right text-sm text-yellow-600'>
                                {t('booking.summary.hairdressingServiceNote', '+ servicio de peluquería')}
                            </span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
